import { useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

type DurationInputProps = {
  durationMinutes: number;
  onCommit: (minutes: number) => void;
};

const MAX_MINUTES = 999;

/**
 * DurationInput — tap-to-edit minutes on a travel leg's AI-estimated
 * duration. Tapping the chip opens a numeric input (1–3 digits) that commits
 * on blur/submit. A value of 0 renders as the "unset" state (location-less
 * stop) for the user to fill in.
 */
export function DurationInput({ durationMinutes, onCommit }: DurationInputProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState('');
  const isUnset = durationMinutes <= 0;

  const beginEditing = () => {
    setText(isUnset ? '' : String(durationMinutes));
    setIsEditing(true);
  };

  const commitEdit = () => {
    setIsEditing(false);
    const parsed = parseInt(text, 10);
    onCommit(Number.isFinite(parsed) ? Math.min(Math.max(0, parsed), MAX_MINUTES) : 0);
  };

  if (isEditing) {
    return (
      <View style={[styles.container, { borderColor: palette.primary }]}>
        <TextInput
          style={[styles.input, { color: palette.text, fontFamily: getBodyFont() }]}
          value={text}
          onChangeText={(next) => setText(next.replace(/[^0-9]/g, '').slice(0, 3))}
          keyboardType="number-pad"
          autoFocus
          selectTextOnFocus
          onBlur={commitEdit}
          onSubmitEditing={commitEdit}
          returnKeyType="done"
        />
        <Text style={[styles.unit, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          min
        </Text>
      </View>
    );
  }

  return (
    <TouchableOpacity
      style={[styles.container, { borderColor: isUnset ? palette.warning : palette.border }]}
      onPress={beginEditing}
      hitSlop={8}
      activeOpacity={0.7}
    >
      <Text
        style={[
          styles.value,
          { color: isUnset ? palette.warning : palette.text, fontFamily: getBodyFont() },
        ]}
      >
        {isUnset ? 'set min' : `${durationMinutes} min`}
      </Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xxs,
    borderWidth: 1,
    borderRadius: BorderRadius.sm,
    paddingHorizontal: Spacing.sm,
    paddingVertical: 2,
    minHeight: 26,
  },
  value: {
    fontSize: 13,
    fontWeight: '600',
    minWidth: 48,
    textAlign: 'center',
  },
  input: {
    fontSize: 13,
    fontWeight: '600',
    minWidth: 36,
    textAlign: 'center',
    padding: 0,
  },
  unit: {
    fontSize: 12,
  },
});
